import { observable, action, computed } from 'mobx'
import { task } from 'mobx-task'
import { Subject, find, firstValueFrom } from 'rxjs'
import { ViewModelList } from '@/infra/view-model-list'
import { toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ApprovedTransactionViewModel } from './ApprovedTransactionViewModel'
import { type ApprovedTransaction } from '@/features/payouts/admin/ApprovedTransaction'
import { type PendingTransaction } from '@/features/payouts/admin/PendingTransaction'
import { DraftPayout } from '@/features/payouts/admin/DraftPayout'
import { type PayoutReviewStore } from '@/features/payouts/admin/PayoutReviewStore'
import { ConfirmPayoutDialogViewModel } from './ConfirmPayoutDialogViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { PreconditionFailed } from 'fejl'
import { noop } from 'lodash-es'
import { csvDownload } from '@/utils/csv-util'
import { mapApprovedTransactionsToCsv } from '@/features/payouts/admin/payout-csv-mappers'

/**
 * Approves pending transactions into the draft payout.
 */
export type ApproveTransactionsFn = (
  transactions: PendingTransaction[],
) => Promise<void>

/**
 * Removes approved transactions from the draft payout.
 */
export type RemoveTransactionsFn = (
  transactions: ApprovedTransaction[],
) => Promise<void>

/**
 * View model for drafting and submitting a payout.
 */
export class PayoutDraftingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Emits when payout completes.
   */
  private readonly _payoutCompleted = new Subject()

  /**
   * The approved transaction view models.
   */
  private readonly _approvedTransactionVms: ViewModelList<
    ApprovedTransaction,
    ApprovedTransactionViewModel
  >

  /**
   * The current draft.
   */
  @observable accessor draft = DraftPayout.none

  /**
   * Emits when payout completes.
   */
  readonly payoutCompleted$ = this._payoutCompleted.asObservable()

  /**
   * Asks for confirmation before submitting.
   */
  readonly confirmPayoutVm = new ConfirmPayoutDialogViewModel(() => ({
    total: this.total,
    transactionCount: this.approvedTransactions.length,
  }))

  constructor(
    private readonly _payoutReviewStore: PayoutReviewStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._approvedTransactionVms = new ViewModelList({
      models: () => this.draft.approvedTransactions.items.slice(),
      create: (model) =>
        new ApprovedTransactionViewModel(model, this.removeTransactions),
    })

    this._lifecycle.manage(this._approvedTransactionVms)
  }

  /**
   * The approved transactions.
   */
  @computed
  get approvedTransactions() {
    return this._approvedTransactionVms.items
  }

  /**
   * Whether approving, removing or submitting is in progress.
   */
  @computed
  get isBusy() {
    return (
      this.approveTransactions.pending ||
      this.removeTransactions.pending ||
      this.submit.pending
    )
  }

  /**
   * Whether we are able to submit.
   */
  @computed
  get canSubmit() {
    if (this.submit.pending) {
      return false
    }

    return this.approvedTransactions.length > 0
  }

  /**
   * The total approved amount.
   */
  @computed
  get total(): number {
    return this.draft.totalAmount
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this.confirmPayoutVm.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the current draft.
   */
  fetch = task(async () => {
    const draft = await this._payoutReviewStore.fetchCurrentDraftPayout(
      this._lifecycle.signal,
    )
    this._setDraft(draft)
  })

  /**
   * Approves the specified transactions.
   */
  approveTransactions = task.resolved(
    async (transactions: PendingTransaction[]) => {
      await this._waitForDraftToLoad()
      await this._payoutReviewStore
        .approveTransactions({ transactions, draft: this.draft })
        .catch(this._createAlertErrorHandler('Approving failed'))
    },
  )

  /**
   * Removes the specified transactions.
   */
  removeTransactions = task.resolved(
    async (transactions: ApprovedTransaction[]) => {
      await this._waitForDraftToLoad()
      await this._payoutReviewStore
        .removeTransactions({ transactions, draft: this.draft })
        .catch(this._createAlertErrorHandler('Removing failed'))
    },
  )

  /**
   * Removes all the approved transactions.
   */
  @action.bound
  removeAll() {
    return this.removeTransactions(
      this.approvedTransactions.map((v) => v.transaction),
    )
  }

  /**
   * Asks for confirmation, then submits the payout.
   */
  @action.bound
  async requestSubmit() {
    if (await this.confirmPayoutVm.prompt(this._lifecycle.signal)) {
      await this.submit()
    }
  }

  /**
   * Submit the payout.
   */
  submit = task.resolved(async () => {
    // There is no progress endpoint to follow, so the payout is polled until
    // it completes.
    const draft = this.draft
    const alert = this._newAlert()
      .saying('Submitting', 'The payout is being submitted...')
      .withState('pending')
      .show()

    try {
      await this._waitForDraftToLoad()
      await this._payoutReviewStore.submitPayout({ draft })
    } catch (err) {
      if (err instanceof PreconditionFailed) {
        // There was a conflict; load the latest version of the draft. A
        // failed reload shows in the drafting view.
        await this.fetch().catch(noop)
        alert
          .withState('warning')
          .saying(
            'Wait a second!',
            'It looks like your copy of the draft was not entirely synchronized. It has been reloaded. Please verify everything looks correct before trying again.',
          )

        return
      }
      alert.dismiss()
      return this._createAlertErrorHandler('Submitting the payout failed')(err)
    }

    alert.saying(
      'Processing',
      'The payout was submitted and is being processed. No need to wait though.',
    )

    try {
      // Wait for the payout to complete.
      while (!(await this._payoutReviewStore.fetchPayoutCompletion(draft.id))) {
        await new Promise((resolve) => setTimeout(resolve, 1_000))
      }

      // After the payout is submitted, the next draft payout is automatically
      // provisioned by the system which becomes the current draft payout.
      // Not bound to the lifecycle signal, so completion is still reported
      // after the admin leaves the tab.
      this._setDraft(await this._payoutReviewStore.fetchCurrentDraftPayout())
    } catch (err) {
      alert.dismiss()
      return this._createAlertErrorHandler('Checking the payout failed')(err)
    }

    alert
      .saying('Payout completed!', 'The payout has completed!')
      .withState('success')
      .autoDismiss()

    this._payoutCompleted.next(undefined)
  })

  /**
   * Exports the approved transactions as CSV.
   */
  @action.bound
  exportCsv() {
    csvDownload(
      mapApprovedTransactionsToCsv(
        this.approvedTransactions.map((avm) => avm.transaction),
      ),
      'approved-transactions.csv',
    )
  }

  /**
   * Waits for the draft to have loaded.
   * This is called before operations that work with the draft.
   */
  private async _waitForDraftToLoad() {
    await firstValueFrom(
      toStream(() => this.draft, true).pipe(
        find((draft) => draft !== DraftPayout.none),
      ),
      {
        defaultValue: null,
      },
    )
  }

  /**
   * Sets the draft.
   */
  @action.bound
  private _setDraft(draft: DraftPayout) {
    this.draft = draft
  }
}
